/**
 * multiTfState — Top-Down per-TF state vector orchestrator.
 *
 * Composes the Phase A primitives (alphaRsi, volumeRegime, stageDetect,
 * alwaysIn, trendIntegrity) plus existing helpers (emas.js, adx.js) into
 * the full per-TF state vector specced in the Top-Down design doc
 * (docs/research/Coach_TopDown_Analysis.md §2.1).
 *
 * Two entry points:
 *   - `computeTimeframeState(bars, opts)` — full state for ONE timeframe.
 *   - `computeMultiTfState(multiTfBars, opts)` — state across all 5 TFs
 *     (Daily / 4H / 1H / 15M / 5M).
 *
 * Each accepts an optional `asOfTime` so callers (trade-entry capture and
 * live polling) can ask "what did the state look like at this exact moment?"
 * Without asOfTime, the state reflects the most recent bar in the input.
 *
 * **Scope of v1:** captures stage / EMA stack + slope / always-in / trend
 * integrity / RSI state / volume regime. Level-proximity (Phase B) and
 * recent-patterns (already exists via setupPatterns) populate in later
 * phases — left as null fields in the v1 output so the schema is forward-
 * compatible.
 *
 * Pure function. All composed primitives are pure. No side effects.
 */

import { computeEmas, emaAtTime } from './emas.js';
import { computeADX } from './adx.js';
import { computeAlphaRsi, alphaRsiStateAtTime } from './alphaRsi.js';
import { volumeRegimeStateAtTime } from './volumeRegime.js';
import { detectStageAt } from './stageDetect.js';
import { detectAlwaysInAt } from './alwaysIn.js';
import { detectTrendIntegrityAt } from './trendIntegrity.js';

/** EMA periods captured per TF — matches Chris's NinjaTrader template (5/9/21/50). */
export const DEFAULT_EMA_PERIODS = [5, 9, 21, 50];

/** The five top-down timeframes. Keys match what the trade pipeline persists. */
export const TF_KEYS = Object.freeze(['daily', 'fourHour', 'oneHour', 'fifteen', 'five']);

/**
 * Truncate a parallel-array OHLCV series to bars at or before `asOfTime`.
 * Defensive: returns the full array when asOfTime is missing/invalid.
 */
const truncateToTime = (bars, asOfTime) => {
  if (!Array.isArray(bars)) return [];
  if (!Number.isFinite(asOfTime)) return bars.slice();
  const out = [];
  for (const b of bars) {
    const t = Number(b?.time);
    if (!Number.isFinite(t)) continue;
    if (t > asOfTime) break;
    out.push(b);
  }
  return out;
};

/**
 * Classify the EMA stack stance from a precomputed snapshot.
 * Bullish = strictly descending periods → ascending values (longest at bottom).
 * Bearish = strictly ascending periods → ascending values (shortest at bottom).
 *
 * @param {Object} snapshot   Map of { period: value | null }.
 * @param {number[]} periods  Periods in the order we want to stack-check.
 * @returns {'bullish' | 'bearish' | 'mixed' | 'unknown'}
 */
const classifyEmaStack = (snapshot, periods) => {
  if (!snapshot || typeof snapshot !== 'object') return 'unknown';
  const sorted = [...periods].sort((a, b) => a - b);  // ascending periods
  const values = sorted.map(p => snapshot[p]);
  if (!values.every(v => Number.isFinite(v))) return 'unknown';
  // Bullish: shortest > longer (price moving up, fastest EMAs lead).
  let bullish = true, bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (bullish) return 'bullish';
  if (bearish) return 'bearish';
  return 'mixed';
};

/**
 * Per-period EMA slope at the cutoff bar. Uses small backward-lookback to
 * decide rising/flat/falling.
 */
const classifyEmaSlope = (series, lookbackBars = 3) => {
  if (!Array.isArray(series) || series.length < 2) return 'flat';
  // Last valid value.
  let endIdx = -1;
  for (let i = series.length - 1; i >= 0; i--) {
    if (Number.isFinite(series[i]?.value)) { endIdx = i; break; }
  }
  if (endIdx < 0) return 'flat';
  const target = endIdx - lookbackBars;
  if (target < 0) return 'flat';
  let startIdx = -1;
  for (let i = target; i >= 0; i--) {
    if (Number.isFinite(series[i]?.value)) { startIdx = i; break; }
  }
  if (startIdx < 0) return 'flat';
  const delta = series[endIdx].value - series[startIdx].value;
  // Tolerance relative to the EMA value itself (basis-point band):
  // 0.05% of the EMA value counts as flat.
  const tol = Math.abs(series[endIdx].value) * 0.0005;
  if (Math.abs(delta) <= tol) return 'flat';
  return delta > 0 ? 'rising' : 'falling';
};

/**
 * Compute the full per-TF state vector for a single timeframe.
 *
 * @param {Array<{time, open, high, low, close, volume}>} bars  OHLCV bars.
 * @param {Object} [opts]
 * @param {number} [opts.asOfTime]  Unix sec; truncate bars to ≤ asOfTime first.
 * @param {number[]} [opts.emaPeriods]  Default [5, 9, 21, 50].
 * @returns {Object}  See Top-Down §2.1 for shape.
 */
export const computeTimeframeState = (bars, opts = {}) => {
  const periods = Array.isArray(opts.emaPeriods) ? opts.emaPeriods : DEFAULT_EMA_PERIODS;
  const slice = truncateToTime(bars, opts.asOfTime);
  // Empty result — return a fully-null state so downstream code can render
  // "no data" without conditional branches everywhere.
  if (slice.length === 0) {
    return {
      stage: 'unclear',
      emaStack: 'unknown',
      emaStackPeriods: periods,
      emaSlopes: {},
      emaValues: {},
      alwaysIn: 'unclear',
      trendIntegrity: 'no-trend',
      rsi: { value: null, signal: null, zone: null, slope: null, signalCross: null },
      volume: { current: null, ma: null, regime: null },
      barsAvailable: 0,
      asOfTime: opts.asOfTime ?? null,
    };
  }

  const lastTime = Number(slice[slice.length - 1]?.time);

  // EMAs.
  const emaSeriesByPeriod = computeEmas(slice, periods);
  const emaSnapshot = {};
  const emaSlopes = {};
  for (const p of periods) {
    emaSnapshot[p] = emaAtTime(emaSeriesByPeriod[p], lastTime);
    emaSlopes[p] = classifyEmaSlope(emaSeriesByPeriod[p]);
  }

  // ADX series for stage detection.
  const adxSeries = computeADX(slice);

  // RSI.
  const rsiComputed = computeAlphaRsi(slice);
  const rsiState = alphaRsiStateAtTime(rsiComputed, lastTime);

  // Volume regime.
  const volumeState = volumeRegimeStateAtTime(slice, lastTime);

  // Stage (Wyckoff) needs precomputed ema21 / ema50 / adx series.
  const stage = detectStageAt(
    slice,
    { ema21: emaSeriesByPeriod[21], ema50: emaSeriesByPeriod[50], adx: adxSeries },
  );

  // Always-in needs ema21.
  const alwaysIn = detectAlwaysInAt(slice, { ema21: emaSeriesByPeriod[21] });

  // Trend integrity needs just bars (handles swing detection + ATR internally).
  const trendIntegrity = detectTrendIntegrityAt(slice);

  return {
    stage,
    emaStack: classifyEmaStack(emaSnapshot, periods),
    emaStackPeriods: periods,
    emaSlopes,
    emaValues: emaSnapshot,
    alwaysIn,
    trendIntegrity,
    rsi: rsiState,
    volume: volumeState,
    barsAvailable: slice.length,
    asOfTime: opts.asOfTime ?? lastTime,
  };
};

/**
 * Compute state for ALL five top-down timeframes.
 *
 * @param {Object} multiTfBars  { daily, fourHour, oneHour, fifteen, five }
 *                              — each an OHLCV bar array (may be missing).
 * @param {Object} [opts]  asOfTime + emaPeriods forwarded to each TF.
 * @returns {Object}  { daily, fourHour, oneHour, fifteen, five } → state.
 */
export const computeMultiTfState = (multiTfBars = {}, opts = {}) => {
  const out = {};
  for (const tf of TF_KEYS) {
    const bars = multiTfBars[tf];
    if (Array.isArray(bars) && bars.length > 0) {
      out[tf] = computeTimeframeState(bars, opts);
    } else {
      // Empty/missing TF — surface a null state so the caller's shape doesn't
      // depend on which TFs were available.
      out[tf] = computeTimeframeState([], opts);
    }
  }
  return out;
};

// Test-friendly internal exports.
export { classifyEmaStack, classifyEmaSlope, truncateToTime };
